import { AspectRatio } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import React from 'react';

import { TextField } from 'Containers/App/types';
import { fetchShowVideoStatus } from 'Containers/App/utils';
import { RiskAversion, Survey } from 'Containers/ProfilePage/types';
import { CommuterBenefitDescription } from 'Containers/ViewBenefitPage/BenefitDetails/commuterContent';
import { ContentfulCustomMessage } from 'ContentfulDefaults/types/_shared';
import { ContentfulSupplementalPlan } from 'ContentfulDefaults/types/benefitsAdditional';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useTextContext } from 'Contexts/textContext';
import { Button, H4, H6, Paragraph } from 'DesignLibrary/atoms';
import { Drawer } from 'DesignLibrary/atoms/Drawer';
import InfoBar from 'DesignLibrary/molecules/InfoBar';
import RichText from 'Shared/RichText';
import { RichTextStyleWrapper } from 'Shared/RichText/styled';
import { SupplementalIcon } from 'Shared/SupplementalIcon';
import Text from 'Shared/Text';
import Video from 'Shared/Video';
import { ReasonsWhy, SupplementalPlan } from 'Types/entities';
import { BuilderGoSettings, CdnPlanInfo } from 'Utils/apiTypes';
import { parseSanitizeHtmlShiftHeadings } from 'Utils/helpers';

import { DCFSADescription } from './dcfsaContent';
import { MedicalFSABenefitDescription } from './medicalFSAContent';
import { ChoosePlanBtnWrapper, DetailsContainer, DetailsPlan } from './styled';
import { CONTENT_MAP, ContentMapElement } from '../constants';
import FbsDetail from '../ExpandablePlanCard/FbsDetail';
import PlanButtons from '../ExpandablePlanCard/PlanButtons';
import { ButtonContainer } from '../ExpandablePlanCard/styled';
import ReasonsWhyList from '../ReasonsWhyList';
import { InsuranceType } from '../types';
import { stripHealthCarePrefix } from '../utils';

export interface BenefitDetailsProps {
  employerName: string;
  insuranceType: InsuranceType;
  // Only meaningful for fsa/fsa_medical insuranceType; ignored otherwise.
  isLimitedPurposeFsa: boolean;
  isOpen: boolean;
  nonSelectablePlans?: SupplementalPlan[];
  contentfulSupplementalPlans: Record<string, ContentfulSupplementalPlan> | Record<string, never>;
  reasonsWhy: ReasonsWhy;
  selectedHealthDeductible: number;
  dependentsCount: number;
  surveyAnswers: Survey;
  riskAversion: RiskAversion;
  planCount?: number;
  planTitle?: string;
  builderCustomerKey: string | null;
  builderGoSettings: BuilderGoSettings | null;
  handleOpenBenefit: (insuranceType: string) => void;
  onClose: () => void;
  cdnPlanDetails?: Record<string, CdnPlanInfo>;
}

const BenefitDetails = ({
  employerName,
  insuranceType,
  isLimitedPurposeFsa,
  isOpen,
  nonSelectablePlans = [],
  contentfulSupplementalPlans,
  reasonsWhy,
  selectedHealthDeductible,
  dependentsCount,
  surveyAnswers,
  riskAversion,
  planCount,
  planTitle,
  builderCustomerKey,
  onClose,
  handleOpenBenefit,
  builderGoSettings,
  cdnPlanDetails,
}: BenefitDetailsProps) => {
  const { retrieveContentfulData } = useTextContext();
  const { is_educational_videos_enabled } = useFeatureFlagContext();

  // Cast to partial so TypeScript allows undefined for unknown types (e.g. custom_module_*)
  const contentEntry = (CONTENT_MAP as Record<string, ContentMapElement | undefined>)[insuranceType];

  // Determine if fields exist in Contentful. If not, use hard-coded values in content map
  const benefitTitle = contentEntry
    ? retrieveContentfulData<string>(`${contentEntry.field}.card_title`, insuranceType)
    : planTitle || '';
  const altBenefitTitle = contentEntry
    ? retrieveContentfulData<string>(`${contentEntry.field}.alternate_title`)
    : null;
  const benefitVideo = contentEntry
    ? retrieveContentfulData<string>(`${contentEntry.field}.video_link`)
    : null;
  const altBenefitVideo = contentEntry
    ? retrieveContentfulData<string>(`${contentEntry.field}.alternate_video_link`)
    : null;
  const benefitNotes = contentEntry
    ? retrieveContentfulData<Record<string, string>>(`${contentEntry.field}.notes`)
    : null;

  // Determine if Limited Purpose FSA and show alternate description if so
  const description = contentEntry?.description;
  const altDescription = contentEntry?.alternate_description as JSX.Element | undefined;
  let revisedTitle = planTitle || benefitTitle;
  let reasonsWhyTitle = revisedTitle;
  let revisedDescription = description;
  let revisedVideo = benefitVideo;

  const isFsaOrFsaMedical = isLimitedPurposeFsa && ['fsa', 'fsa_medical'].includes(insuranceType);

  if (isFsaOrFsaMedical) {
    revisedTitle = altBenefitTitle as string;
    reasonsWhyTitle = stripHealthCarePrefix(altBenefitTitle) ?? revisedTitle;
    revisedDescription = altDescription;
    revisedVideo = altBenefitVideo;
  }

  const renderReasonsWhy = () => {
    if (isEmpty(reasonsWhy[insuranceType])) {
      return null;
    }

    return (
      <ReasonsWhyList
        insuranceType={insuranceType}
        reasonsWhy={reasonsWhy[insuranceType] ?? []}
        healthDeductible={selectedHealthDeductible}
        childrenCount={dependentsCount}
        capacityToPay={surveyAnswers.capacity_to_pay}
        riskAversion={riskAversion}
        planTitle={reasonsWhyTitle}
      />
    );
  };

  const renderViewPlansButton = () => {
    if (planCount) {
      return (
        <ChoosePlanBtnWrapper>
          <Button
            buttonType="primary"
            iconRight="ArrowRight"
            data-testid={`choose-${insuranceType}-btn`}
            className={`open-benefit-btn ${`pingdom-choose-${insuranceType}`}`}
            onClick={() => handleOpenBenefit(insuranceType)}
            stretch
          >
            <Text field="benefits_section.button_text.viewPlans" />
          </Button>
        </ChoosePlanBtnWrapper>
      );
    }
    return null;
  };

  const renderCustomMessage = (plan: SupplementalPlan) => {
    const contentfulSupplementalPlans = retrieveContentfulData<ContentfulSupplementalPlan[]>(
      'benefits_section_additional.supplemental_plans',
      [],
    );

    const customMessageIdx = contentfulSupplementalPlans.findIndex((i) => i.plan_id === plan.external_id);

    if (customMessageIdx >= 0) {
      const customMessage = contentfulSupplementalPlans[customMessageIdx]
        .custom_message as ContentfulCustomMessage;

      if (!customMessage) {
        return null;
      }
      return (
        <InfoBar
          className={`${plan.external_id}-customMessage`}
          text={
            <RichText
              inlineParagraphs
              field={
                `benefits_section_additional.supplemental_plans[${customMessageIdx}].custom_message.text` as TextField
              }
            />
          }
          color={customMessage?.color}
          header={customMessage?.header}
          stretch
          icon={customMessage?.icon}
          buttons={[{ text: customMessage?.url_button_text, url: customMessage?.url }]}
        />
      );
    }
    return null;
  };

  const renderDescription = (plan: SupplementalPlan) => {
    const cdnDetails = cdnPlanDetails?.[plan.external_id]?.details;
    if (cdnDetails) {
      return (
        <RichTextStyleWrapper className="plan-description" p={{ size: 'small', lineHeight: 1.75 }}>
          {parseSanitizeHtmlShiftHeadings(cdnDetails)}
        </RichTextStyleWrapper>
      );
    }
    if (isEmpty(plan.benefits?.description) && !contentfulSupplementalPlans?.[plan.external_id]) {
      return null;
    }
    if (!builderCustomerKey && plan?.benefits?.description) {
      return (
        <Paragraph lineHeight={1.75} size="small" className="plan-description">
          <FbsDetail
            type={plan.benefits.description.type}
            data={plan.benefits.description.data}
            richTextProps={{
              maxHeaderSize: 4,
              useStylingWrapper: true,
              richTextStyling: { p: { size: 'small' } },
            }}
          />
        </Paragraph>
      );
    }

    // TODO: Use this variable somewhere
    // eslint-disable-next-line no-constant-condition
    if (false) {
      return employerName;
    }
    if (contentfulSupplementalPlans[plan.external_id]?.description) {
      return (
        <Paragraph as="div" className="plan-description">
          <RichText
            inlineParagraphs={false}
            field={undefined}
            fieldContent={contentfulSupplementalPlans[plan.external_id].description}
            useStylingWrapper
            richTextStyling={{ p: { size: 'small', lineHeight: 1.75 } }}
            maxHeaderSize={4}
          />
        </Paragraph>
      );
    }
    let desc: JSX.Element | null | string = null;
    if (plan.plan_type === 'commuter') {
      desc = <CommuterBenefitDescription plan={plan} />;
    } else if (plan.plan_type === 'fsa' || plan.plan_type === 'fsa_medical') {
      desc = <MedicalFSABenefitDescription plan={plan} isLimitedPurpose={isFsaOrFsaMedical} />;
    } else if (plan.plan_type === 'fsa_dependent') {
      desc = <DCFSADescription plan={plan} />;
    } else if (contentfulSupplementalPlans?.[plan.external_id]) {
      desc = (
        <Paragraph as="div" className="plan-description">
          <RichText
            inlineParagraphs={false}
            field={undefined}
            fieldContent={contentfulSupplementalPlans[plan.external_id].description}
            useStylingWrapper
            richTextStyling={{ p: { size: 'small', lineHeight: 1.75 } }}
            maxHeaderSize={4}
          />
        </Paragraph>
      );
    } else if (plan?.benefits?.description) {
      desc = (
        <Paragraph lineHeight={1.75} size="small" className="plan-description">
          <FbsDetail
            type={plan.benefits.description.type}
            data={plan.benefits.description.data}
            richTextProps={{
              maxHeaderSize: 4,
              useStylingWrapper: true,
              richTextStyling: { p: { size: 'small' } },
            }}
          />
        </Paragraph>
      );
    }

    return desc;
  };
  const shouldShowEducationalVideo =
    !is_educational_videos_enabled ||
    (builderGoSettings && !!builderGoSettings[fetchShowVideoStatus(insuranceType)]);

  return (
    <Drawer
      title={revisedTitle as string}
      titleIcon={<SupplementalIcon type={insuranceType} size="small" />}
      isOpen={isOpen}
      onClose={onClose}
    >
      <DetailsContainer data-testid={`${insuranceType}-drawer`}>
        <div className="reasons-why">{renderReasonsWhy()}</div>

        {nonSelectablePlans.length > 0 &&
          !insuranceType.startsWith('custom_module_') &&
          !['fsa', 'fsa_medical', 'fsa_dependent'].includes(insuranceType) && (
            <InfoBar
              text={<RichText inlineParagraphs field="benefits_section_additional.no_plans_info_bar.text" />}
            />
          )}
        {shouldShowEducationalVideo && revisedVideo && (
          <div className="video-container">
            <AspectRatio ratio={16 / 9} data-testid={`${insuranceType}-video`}>
              <Video
                title={`${benefitTitle} video`}
                url={revisedVideo as string}
                width="100%"
                height="100%"
              />
            </AspectRatio>
          </div>
        )}

        {nonSelectablePlans.length > 0 &&
          !['fsa', 'fsa_medical', 'fsa_dependent'].includes(insuranceType) && (
            <>
              <Paragraph>
                <Text field="benefits_section_additional.non_selectable_plans_label" />
              </Paragraph>
              <section className="detail-plans">
                {nonSelectablePlans.map((plan, idx) => (
                  <DetailsPlan key={`${plan.external_id}-details`}>
                    {!insuranceType.startsWith('custom_module_') && (
                      <div className="header-text">
                        <Paragraph size="small" className="carrier-name">
                          {plan.carrier_name}
                        </Paragraph>
                        <H4 as="h3" className="plan-name">
                          {cdnPlanDetails
                            ? cdnPlanDetails[plan.external_id]?.nickname || plan.name || revisedTitle
                            : revisedTitle}
                        </H4>
                      </div>
                    )}

                    {renderCustomMessage(plan)}
                    {renderDescription(plan)}

                    {contentfulSupplementalPlans?.[plan.external_id]?.benefit_items && (
                      <div className="benefit-items">
                        {contentfulSupplementalPlans[plan.external_id].benefit_items.map((item, idx) => {
                          const [name, details = ''] = item.split(':');
                          return (
                            <React.Fragment key={idx}>
                              <h4>{name}</h4>
                              <Paragraph lineHeight={1.75} size="small">
                                {details}
                              </Paragraph>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}

                    {plan.pdf_url && (
                      <ButtonContainer className="button-container">
                        <PlanButtons
                          plan={plan}
                          idx={idx}
                          insuranceType={insuranceType}
                          isSelected={false}
                          isAutoEnrolled={false}
                          isEligible={false}
                          hasNoSelection
                          // hasNoSelection -> don't need functions to handle selection
                          handleSelect={() => null}
                          handleRemovePlan={() => null}
                          handleTrackPdfView={() => null}
                        />
                      </ButtonContainer>
                    )}
                  </DetailsPlan>
                ))}
              </section>
            </>
          )}

        <div className="main-description">
          <Paragraph lineHeight={1.75}>{revisedDescription}</Paragraph>
          <Paragraph lineHeight={1.75}>{contentEntry?.info}</Paragraph>

          {benefitNotes && (
            <div className="notes">
              <H6>{benefitNotes.title}</H6>
              <Paragraph lineHeight={1.75}>{benefitNotes.note}</Paragraph>
            </div>
          )}
        </div>

        {renderViewPlansButton()}
      </DetailsContainer>
    </Drawer>
  );
};

export default BenefitDetails;
